import { Link } from '@tanstack/react-router';
import { CodeBlock, Callout } from '../../components';

export default function Content() {
  return (
    <>
      <p>
        glTF and GLB are the same 3D format, <strong>glTF 2.0</strong>, packaged in two different ways. A{' '}
        <code>.gltf</code> file is readable JSON that points to separate <code>.bin</code> geometry and texture
        files, while a <code>.glb</code> is one binary file with everything inside. Use GLB to deliver models to
        websites, AR and online stores, and use <code>.gltf</code> when you want to read, hand-edit or diff the
        scene.
      </p>
      <p>
        Because the underlying data is identical, you can convert between the two without losing anything, and
        every serious glTF loader (three.js, Babylon.js, Blender, Godot and others) reads both. The choice is
        about packaging, not quality.
      </p>

      <h2>Same data, different packaging</h2>
      <p>
        A glTF asset always has two parts: a <strong>JSON scene description</strong> (nodes, meshes, materials,
        animations, cameras) and <strong>binary data</strong> (vertex positions, normals, UVs, indices, animation
        keyframes and texture images). The two file types only differ in where that binary data lives.
      </p>

      <h3>What's in a .gltf file</h3>
      <p>
        A <code>.gltf</code> is plain JSON. The binary data sits in separate files that the JSON references by
        relative path, so a typical export looks like <code>chair.gltf</code>, <code>chair.bin</code> and a
        handful of PNG or JPEG textures:
      </p>
      <CodeBlock language="json">{`{
  "asset": { "version": "2.0" },
  "buffers": [
    { "uri": "chair.bin", "byteLength": 1048576 }
  ],
  "images": [
    { "uri": "chair_basecolor.jpg" },
    { "uri": "chair_normal.png" }
  ]
}`}</CodeBlock>
      <p>
        There's also an <strong>embedded</strong> variant where the <code>.bin</code> and images are stuffed into
        the JSON as base64 data URIs, giving you a single <code>.gltf</code> file. The glTF specification notes
        that base64 encoding increases the payload's byte length by 33%, and the data has to be decoded before
        use. Blender still offers this option, but hides it by default and calls it the least efficient form. If
        you want a single file, use GLB instead.
      </p>

      <h3>What's in a .glb file</h3>
      <p>
        A <code>.glb</code> (glTF Binary) is a small container: a 12-byte header starting with the magic bytes{' '}
        <code>glTF</code>, then a JSON chunk with the same scene description, then a binary chunk holding the
        buffer data. Textures are stored inside that binary chunk too, and the JSON points at them by byte range
        instead of by file name:
      </p>
      <CodeBlock language="json">{`"buffers": [{ "byteLength": 1363968 }],
"images": [
  { "bufferView": 4, "mimeType": "image/jpeg" },
  { "bufferView": 5, "mimeType": "image/png" }
]`}</CodeBlock>
      <p>
        The spec does allow a GLB to reference external files (for example, to keep images separate), but in
        practice most GLB files are fully self-contained. For a closer look at the format, see{' '}
        <Link to="/blog/what-is-a-glb-file">what is a GLB file</Link>.
      </p>

      <h2>glTF vs GLB at a glance</h2>
      <table>
        <thead>
          <tr><th></th><th>.gltf (separate files)</th><th>.glb</th></tr>
        </thead>
        <tbody>
          <tr><td>Files</td><td>One .gltf plus .bin and texture files</td><td>One file</td></tr>
          <tr><td>Human-readable</td><td>Yes, open it in any text editor</td><td>No, it's binary</td></tr>
          <tr><td>Hand-editing</td><td>Easy: change a value or a texture path and save</td><td>Convert to .gltf first, or use a tool</td></tr>
          <tr><td>Version control</td><td>JSON changes show up as readable diffs</td><td>Any change is an opaque binary diff</td></tr>
          <tr><td>File size</td><td>Same total as GLB; embedded (base64) data is about 33% larger</td><td>Compact, no encoding overhead</td></tr>
          <tr><td>HTTP requests</td><td>One for the JSON plus one per .bin and texture</td><td>One</td></tr>
          <tr><td>Caching</td><td>Per file; a texture shared by several models downloads once</td><td>Cached as one unit; any change invalidates the whole file</td></tr>
          <tr><td>Broken references</td><td>Missing textures if a file is renamed, moved or not uploaded</td><td>Not an issue, everything travels together</td></tr>
          <tr><td>Tool support</td><td>All glTF loaders; not accepted by some upload platforms</td><td>All glTF loaders and most platforms</td></tr>
          <tr><td>MIME type</td><td><code>model/gltf+json</code></td><td><code>model/gltf-binary</code></td></tr>
        </tbody>
      </table>

      <h2>When to use GLB</h2>
      <p>GLB is the right default whenever a model leaves your hands:</p>
      <ul>
        <li>
          <strong>Websites.</strong> One request, one file to cache and one path to reference in your code. See{' '}
          <Link to="/blog/threejs-load-glb">how to load a GLB in three.js</Link>.
        </li>
        <li>
          <strong>AR.</strong> Android's Scene Viewer and the <code>&lt;model-viewer&gt;</code> web component load
          glTF from a URL, and a GLB gives them one self-contained URL to load.
        </li>
        <li>
          <strong>E-commerce.</strong> Shopify accepts 3D product models as GLB or USDZ, not <code>.gltf</code>.
          More in <Link to="/blog/shopify-3d-models">3D models on Shopify</Link>.
        </li>
        <li>
          <strong>Sharing and uploading.</strong> Sending a client or a marketplace a single file removes the
          classic "the textures are missing" problem.
        </li>
      </ul>

      <h2>When to use .gltf</h2>
      <p><code>.gltf</code> with separate files is better while you're still working on an asset:</p>
      <ul>
        <li>
          <strong>Debugging.</strong> Open the JSON to see which extensions a model uses, what a material's factors
          are, or why a texture isn't showing up.
        </li>
        <li>
          <strong>Hand edits.</strong> Fix a texture path, rename a node or tweak a color without re-exporting from
          your 3D app.
        </li>
        <li>
          <strong>Version control.</strong> Git can show what changed in the JSON between two exports; the
          geometry and textures stay as separate files you can replace one at a time.
        </li>
        <li>
          <strong>Swapping or sharing textures.</strong> Replace an image file without touching the geometry, or
          let several models reference the same texture so browsers download it once.
        </li>
        <li>
          <strong>Lazy loading.</strong> Tools like gltf-transform can split the binary data into several{' '}
          <code>.bin</code> files so an app fetches only what it needs. Its <code>partition</code> command works on{' '}
          <code>.gltf</code>, not <code>.glb</code>.
        </li>
      </ul>
      <p>
        To check what's inside a file in either format, run it through our free{' '}
        <Link to="/gltf-validator">glTF validator</Link>. It shows errors, warnings, triangle counts, draw calls and
        the extensions in use.
      </p>

      <h2>How to convert glTF to GLB</h2>
      <p>
        The quickest way is our free <Link to="/gltf-to-glb">glTF to GLB converter</Link>. Select the{' '}
        <code>.gltf</code> together with its <code>.bin</code> and texture files, and download a single GLB. It
        runs entirely in your browser, so nothing is uploaded and you don't need an account.
      </p>
      <p>If you'd rather script it, the open-source gltf-transform CLI does the same:</p>
      <CodeBlock language="bash">{`npx @gltf-transform/cli copy model.gltf model.glb`}</CodeBlock>
      <p>
        In Blender, import the file with File &gt; Import &gt; glTF 2.0, then export with File &gt; Export &gt;
        glTF 2.0 and set the format to glTF Binary (.glb).
      </p>

      <h2>How to convert GLB to glTF</h2>
      <p>The same gltf-transform command works in reverse. The output format follows the file extension:</p>
      <CodeBlock language="bash">{`npx @gltf-transform/cli copy model.glb model.gltf`}</CodeBlock>
      <p>
        This writes <code>model.gltf</code> plus a <code>.bin</code> and the texture images next to it. In
        Blender, import the GLB and export with the glTF Separate (.gltf + .bin + textures) format.
      </p>
      <Callout title="Draco-compressed files grow when you copy them">
        gltf-transform's <code>copy</code> command removes Draco compression to avoid a lossy decompress and
        recompress round trip. If you unpack a Draco GLB to <code>.gltf</code> for editing, expect a larger
        file, and compress again once you're done.
      </Callout>

      <h2>Does the format change file size or compression?</h2>
      <p>
        Not really. A GLB and a separate-files <code>.gltf</code> of the same model add up to roughly the same
        number of bytes; GLB just puts them in one file. Converting glTF to GLB won't make a heavy model light.
        Only the embedded, base64 <code>.gltf</code> is meaningfully bigger.
      </p>
      <p>
        Real size savings come from what's inside: <Link to="/blog/draco-compression">Draco geometry
        compression</Link>, smaller textures in WebP or AVIF, and removing duplicate or unused data. All of these
        work the same on both formats, because they change the glTF data, not the packaging. The full list is in{' '}
        <Link to="/blog/reduce-glb-file-size">how to reduce GLB file size</Link>.
      </p>
      <p>
        When you're ready to ship, our <Link to="/gltf-compressor">glTF and GLB compressor</Link> applies Draco,
        texture resizing and WebP/AVIF conversion in one pass and accepts either format. Typical results are
        60–90% smaller, and the first compression is free with an account.
      </p>

      <h2>FAQ</h2>

      <h3>Is GLB better than glTF?</h3>
      <p>
        For delivery, usually yes: one file, one request and no missing textures. For editing and debugging,{' '}
        <code>.gltf</code> is more convenient because you can read and change the JSON directly. Neither is
        higher quality, since both hold the same glTF 2.0 data.
      </p>

      <h3>Is GLB smaller than glTF?</h3>
      <p>
        Only compared with an embedded <code>.gltf</code>, where base64 adds about 33% to the binary data.
        Compared with a <code>.gltf</code> plus separate <code>.bin</code> and texture files, the total size is
        about the same.
      </p>

      <h3>Can I open a GLB file in a text editor?</h3>
      <p>
        You'll see the JSON near the start of the file, but the rest is binary, and editing it by hand breaks
        the chunk lengths. Convert it to <code>.gltf</code>, edit, and convert back.
      </p>

      <h3>Does three.js load both glTF and GLB?</h3>
      <p>
        Yes. three.js's <code>GLTFLoader</code> loads <code>.gltf</code> and <code>.glb</code> files; for a{' '}
        <code>.gltf</code> it fetches the referenced <code>.bin</code> and textures relative to the JSON's URL.
      </p>

      <h3>Should I upload glTF or GLB to Shopify?</h3>
      <p>
        GLB. Shopify's 3D model media accepts GLB and USDZ files, and it creates the other format automatically
        so the model works on both Android and iOS.
      </p>
    </>
  );
}
